import React, { useEffect, useState } from "react";

import { useHistory } from "react-router-dom";

import useGetTasksV4 from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTasks";
import { PaginationHeaders } from "@fartherfinance/frontend/api/utils/parsePaginationHeaders";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import CreateTaskModal from "@src/multiCustodian/pages/Advisor/TaskWorkflows/CreateTaskModal/CreateTaskModal";
import { trackEvent } from "@src/multiCustodian/services/tracking";

import AdvisorContainer from "./AdvisorContainer";
import AdvisorContentContainer from "./AdvisorContentContainer";
import {
  TasksFiltersProviderV4,
  useTasksFiltersV4,
} from "./TaskWorkflows/TasksFiltersContext";
import TasksTable from "./TaskWorkflows/TasksTable/TasksTable";
import TasksToolbar from "./TaskWorkflows/TasksToolbar/TasksToolbar";

const TaskWorkflowsInternal: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const history = useHistory();
  const [paginationState, setPaginationState] = useState<PaginationHeaders>();
  const { queryParams } = useTasksFiltersV4();

  const auth = useAdvisorRequestAuth();
  const tasksQueryV4 = useGetTasksV4(queryParams, auth);
  const { data, isLoading, hasError } = tasksQueryV4;
  const { values: tasks = [], pagination } = data ?? {};
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);

  useEffect(() => {
    // NOTE: we have to persist pagination values locally as they become undefined inbetween request
    if (pagination) {
      setPaginationState(pagination);
    }
  }, [pagination]);

  return (
    <AdvisorContainer>
      <AdvisorContentContainer
        hasPadding={false}
        heroTitle="Tasks"
        heroEnd={
          <Button
            sx={{ width: 140 }}
            variant="contained"
            size="medium"
            buttonType="primary"
            text="Create Task"
            onClick={() => setShowCreateTaskModal(true)}
          />
        }
      >
        {showCreateTaskModal && (
          <CreateTaskModal
            isOpen={showCreateTaskModal}
            onClose={() => setShowCreateTaskModal(false)}
            onSubmit={(values) => {
              setShowCreateTaskModal(false);
              history.push({
                pathname: "/Advisor/Tasks/CreateTask",
                search: location.search,
                state: {
                  taskType: values.taskType,
                  client: values.client,
                },
              });
            }}
          />
        )}

        <TasksToolbar advisorId={auth?.advisorId} />

        <TasksTable
          tasks={tasks}
          isLoading={isLoading}
          isErrored={hasError}
          pagination={paginationState}
        />

        {children}
      </AdvisorContentContainer>
    </AdvisorContainer>
  );
};

const TaskWorkflows: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  useEffect(() => {
    trackEvent({ name: "Advisor Open Tasks List" });
  }, []);

  return (
    <TasksFiltersProviderV4>
      <TaskWorkflowsInternal>{children}</TaskWorkflowsInternal>
    </TasksFiltersProviderV4>
  );
};

export default TaskWorkflows;
